import './QuestionForm.css';
import { useState, useEffect } from 'react';
import { searchBooks } from '../../../services/bookService';
import { searchAuthors } from '../../../services/authorService';

function MultichoiceForm({ onSubmit, onCancel, initialData, isEdit }) {
    const [questionText, setQuestionText] = useState(initialData?.questionText || '');
    const [choices, setChoices] = useState(
        initialData?.choices || [
            { id: 'a', text: '', note: '' },
            { id: 'b', text: '', note: '' },
            { id: 'c', text: '', note: '' },
            { id: 'd', text: '', note: '' }
        ]
    );
    const [correctAnswer, setCorrectAnswer] = useState(initialData?.correctAnswer || '');

    // Combined search for books and authors
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [searchResults, setSearchResults] = useState({ books: [], authors: [] });
    const [selectedItems, setSelectedItems] = useState(initialData?.selectedItems || []); // Array of { type: 'book'|'author', data: {...} }
    const [searching, setSearching] = useState(false);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Search both books and authors
    useEffect(() => {
        const performSearch = async () => {
            if (debouncedSearch.trim().length < 2) {
                setSearchResults({ books: [], authors: [], showUnlinked: false });
                return;
            }

            try {
                setSearching(true);

                const term = debouncedSearch.trim();
                const bookFilters = { title: term, authorName: term };
                const [bookResults, authorResults] = await Promise.all([
                    searchBooks(bookFilters, 0, 5),
                    searchAuthors(term, 0, 5)
                ]);
                const showUnlinked = 'literární teorie'.includes(term.toLowerCase());

                setSearchResults({
                    books: bookResults.content || [],
                    authors: authorResults.content || [],
                    showUnlinked
                });
            } catch (err) {
                console.error('Error searching:', err);
                setSearchResults({ books: [], authors: [], showUnlinked: false });
            } finally {
                setSearching(false);
            }
        };

        performSearch();
    }, [debouncedSearch]);

    const handleChoiceChange = (index, value) => {
        const newChoices = [...choices];
        newChoices[index].text = value;
        setChoices(newChoices);
    };

    const handleNoteChange = (index, value) => {
        const newChoices = [...choices];
        newChoices[index].note = value;
        setChoices(newChoices);
    };

    const handleAddChoice = () => {
        const nextLetter = String.fromCharCode(97 + choices.length); // a, b, c, d, e, ...
        setChoices([...choices, { id: nextLetter, text: '', note: '' }]);
    };

    const handleRemoveChoice = (index) => {
        if (choices.length <= 2) {
            alert('Otázka musí mít alespoň 2 možnosti odpovědi');
            return;
        }
        const newChoices = choices.filter((_, i) => i !== index);
        setChoices(newChoices);

        // If removed choice was the correct answer, clear it
        if (correctAnswer === choices[index].id) {
            setCorrectAnswer('');
        }
    };

    const handleSelectBook = (book) => {
        // Check if already selected
        const alreadySelected = selectedItems.some(
            item => item.type === 'book' && item.data.id === book.id
        );
        if (alreadySelected) {
            alert('Tato kniha je již vybrána');
            return;
        }
        setSelectedItems([...selectedItems, { type: 'book', data: book }]);
        setSearchTerm('');
        setSearchResults({ books: [], authors: [], showUnlinked: false });
    };

    const handleSelectAuthor = (author) => {
        // Check if already selected
        const alreadySelected = selectedItems.some(
            item => item.type === 'author' && item.data.id === author.id
        );
        if (alreadySelected) {
            alert('Tento autor je již vybrán');
            return;
        }
        setSelectedItems([...selectedItems, { type: 'author', data: author }]);
        setSearchTerm('');
        setSearchResults({ books: [], authors: [], showUnlinked: false });
    };

    const handleSelectUnlinked = () => {
        const alreadySelected = selectedItems.some(item => item.type === 'unlinked');
        if (alreadySelected) return;
        setSelectedItems([...selectedItems, { type: 'unlinked' }]);
        setSearchTerm('');
        setSearchResults({ books: [], authors: [], showUnlinked: false });
    };

    const handleRemoveSelected = (index) => {
        setSelectedItems(selectedItems.filter((_, i) => i !== index));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Validation
        if (!questionText.trim()) {
            alert('Zadejte znění otázky');
            return;
        }

        const filledChoices = choices.filter(c => c.text.trim());
        if (filledChoices.length < 2) {
            alert('Zadejte alespoň 2 možnosti odpovědi');
            return;
        }

        if (!correctAnswer) {
            alert('Vyberte správnou odpověď');
            return;
        }

        const questionData = {
            type: 'Multichoice',
            questionText: questionText.trim(),
            choices: filledChoices.map(c => ({
                id: c.id,
                text: c.text,
                note: c.note || null
            })),
            correctAnswer,
            relatedBooks: selectedItems.filter(item => item.type === 'book').map(item => item.data.id),
            relatedAuthors: selectedItems.filter(item => item.type === 'author').map(item => item.data.id)
        };

        onSubmit(questionData);
    };

    return (
        <form className="question-form" onSubmit={handleSubmit}>
            {/* Question Text */}
            <div className="form-group">
                <label htmlFor="questionText" className="form-label required">
                    Znění otázky
                </label>
                <textarea
                    id="questionText"
                    className="form-textarea"
                    rows="3"
                    value={questionText}
                    onChange={(e) => setQuestionText(e.target.value)}
                    placeholder="Zadejte text otázky..."
                    required
                />
            </div>

            {/* Choices */}
            <div className="form-group">
                <label className="form-label required">Možnosti odpovědi</label>
                <div className="choices-list">
                    {choices.map((choice, index) => (
                        <div key={choice.id} style={{ marginBottom: '0.75rem' }}>
                            <div
                                className="choice-item"
                                onClick={() => {
                                    if (correctAnswer === choice.id) {
                                        setCorrectAnswer('');
                                    } else {
                                        setCorrectAnswer(choice.id);
                                    }
                                }}
                                style={{
                                    backgroundColor: correctAnswer === choice.id ? '#2d5f3f' : '#0f3460',
                                    borderColor: correctAnswer === choice.id ? '#4caf50' : '#1a1a2e',
                                    cursor: 'pointer'
                                }}
                            >
                                <input
                                    type="text"
                                    className="choice-input"
                                    value={choice.text}
                                    onChange={(e) => handleChoiceChange(index, e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    placeholder={`Možnost ${choice.id.toUpperCase()}`}
                                />
                                {choices.length > 2 && (
                                    <button
                                        type="button"
                                        className="btn-remove-choice"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveChoice(index);
                                        }}
                                        title="Odebrat možnost"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                            <input
                                type="text"
                                className="choice-note-input"
                                value={choice.note}
                                onChange={(e) => handleNoteChange(index, e.target.value)}
                                placeholder="Poznámka k odpovědi (nepovinné)"
                            />
                        </div>
                    ))}
                </div>
                {choices.length < 8 && (
                    <button
                        type="button"
                        className="btn-add-choice"
                        onClick={handleAddChoice}
                    >
                        + Přidat možnost
                    </button>
                )}
                <p className="form-hint">
                    Klikněte na možnost pro označení jako správné odpovědi
                </p>
            </div>

            {/* Related Books and Authors */}
            <div className="form-group">
                <label className="form-label">Související knihy a autoři (nepovinné)</label>

                {/* Selected Items */}
                {selectedItems.length > 0 && (
                    <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {selectedItems.map((item, index) => (
                            <div key={index} className="selected-item">
                                <div className="selected-item-info">
                                    <span className="selected-item-type">
                                        {item.type === 'book' ? 'Kniha' : item.type === 'unlinked' ? 'Kategorie' : 'Autor'}
                                    </span>
                                    <span className="selected-item-title">
                                        {item.type === 'book'
                                            ? item.data.bookTitle
                                            : item.type === 'unlinked'
                                            ? 'Literární teorie'
                                            : item.data.name}
                                    </span>
                                    {item.type === 'book' && item.data.author?.name && (
                                        <span className="selected-item-subtitle">
                                            {item.data.author.name}
                                        </span>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className="btn-remove-selected"
                                    onClick={() => handleRemoveSelected(index)}
                                >
                                    ✕
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {/* Search Input */}
                <input
                    type="text"
                    className="form-input"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Vyhledat knihu nebo autora..."
                />
                {searching && <p className="search-loading">Hledám...</p>}
                {(searchResults.books.length > 0 || searchResults.authors.length > 0 || searchResults.showUnlinked) && (
                    <div className="search-results">
                        {searchResults.showUnlinked && (
                            <div
                                className="search-result-item"
                                onClick={handleSelectUnlinked}
                            >
                                <span className="result-title">Literární teorie</span>
                                <span className="result-subtitle">Otázky o literární teorii</span>
                            </div>
                        )}
                        {searchResults.books.length > 0 && (
                            <>
                                <div className="search-results-section-header">Knihy</div>
                                {searchResults.books.map(book => (
                                    <div
                                        key={`book-${book.id}`}
                                        className="search-result-item"
                                        onClick={() => handleSelectBook(book)}
                                    >
                                        <span className="result-title">{book.bookTitle}</span>
                                        <span className="result-subtitle">{book.author?.name}</span>
                                    </div>
                                ))}
                            </>
                        )}
                        {searchResults.authors.length > 0 && (
                            <>
                                <div className="search-results-section-header">Autoři</div>
                                {searchResults.authors.map(author => (
                                    <div
                                        key={`author-${author.id}`}
                                        className="search-result-item"
                                        onClick={() => handleSelectAuthor(author)}
                                    >
                                        <span className="result-title">{author.name}</span>
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Form Actions */}
            <div className="form-actions">
                <button type="submit" className="btn-submit">
                    {isEdit ? 'Uložit změny' : 'Vytvořit otázku'}
                </button>
                <button type="button" className="btn-cancel-form" onClick={onCancel}>
                    Zrušit
                </button>
            </div>
        </form>
    );
}

export default MultichoiceForm;
